/**
 * DaySelectionGrid Component
 * 
 * Renders a grid of days and meal types for selecting where to repeat meals.
 * Handles selection state, disabled states, and displays existing meals.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { getPrimaryMeal } from '@utils/Meals';
import { createRange } from '@utils/MealRepetition';
import { useMealPlanData, useAssets } from '../../contexts';

function DaySelectionGrid({
    ogOffset,
    ogMealType,
    idsToRepeat,
    onToggleSelection,
    isSelected,
    isDisabled
}) {
    // Get data from contexts instead of props
    const { plan, mealTypes, maxOffset } = useMealPlanData();
    const { recipes, foods } = useAssets();
    return (
        <div className="upcoming-dates-picker">
            {createRange(0, maxOffset).map(offset => (
                <div key={offset}>
                    <h4>Day {offset + 1}</h4>
                    <ul>
                        {mealTypes.map(mealType => {
                            const mealTypeName = mealType.name || mealType;
                            const mealTypeDateMeals = (plan?.items || []).filter(
                                m => m.meal === mealTypeName && m.offset === offset
                            );

                            const { primary, titles } = getPrimaryMeal(mealTypeDateMeals, recipes, foods);
                            const isOgMeal = offset === ogOffset && mealTypeName === ogMealType;
                            const isOgLeftover = mealTypeDateMeals.some(m => 
                                idsToRepeat.includes(m.parent)
                            );
                            const disabled = isDisabled(offset, mealTypeName);
                            const selected = isSelected(offset, mealTypeName);

                            return (
                                <li key={mealTypeName}>
                                    <button 
                                        className="meal-toggle-btn"
                                        onClick={() => onToggleSelection(offset, mealTypeName)}
                                        disabled={disabled}
                                        data-selected={isOgMeal || selected}
                                        aria-label={`${isOgMeal ? 'Current meal - ' : ''}${mealTypeName} on Day ${offset + 1}`}
                                    >
                                        {isOgMeal && <span>CURRENT &mdash; </span>}
                                        <em>{mealTypeName}</em>
                                        {mealTypeDateMeals.length > 0 && (
                                            <span>
                                                {primary.meal_type === 'leftover' ? 'LEFTOVER: ' : ''}
                                                {titles.join(' + ')}
                                            </span>
                                        )}
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            ))}
        </div>
    );
}

DaySelectionGrid.propTypes = {
    ogOffset: PropTypes.number,
    ogMealType: PropTypes.string,
    idsToRepeat: PropTypes.arrayOf(PropTypes.string).isRequired,
    onToggleSelection: PropTypes.func.isRequired,
    isSelected: PropTypes.func.isRequired,
    isDisabled: PropTypes.func.isRequired
};

DaySelectionGrid.defaultProps = {
    ogOffset: null,
    ogMealType: null
};

export default DaySelectionGrid;